import { type MouseEvent } from "react";
import type { PlanEvent } from "../../../../shared/types";
import { CATEGORY_LABELS } from "../../../../shared/types";

interface EventItemHeaderProps {
  readonly event: PlanEvent;
  readonly isExpanded: boolean;
  readonly linkedTaskCount?: number;
  readonly linkedDoneCount?: number;
  readonly editingField: string | null;
  readonly editValue: string;
  readonly onToggleExpanded: () => void;
  readonly onStartEdit?: (field: string, value: string) => void;
  readonly onSaveEdit?: () => void;
  readonly onEditValueChange?: (value: string) => void;
  readonly onDelete?: () => void;
}

export function EventItemHeader({
  event,
  isExpanded,
  linkedTaskCount = 0,
  linkedDoneCount = 0,
  editingField,
  editValue,
  onToggleExpanded,
  onStartEdit,
  onSaveEdit,
  onEditValueChange,
  onDelete,
}: EventItemHeaderProps) {
  return (
    <div
      onClick={onToggleExpanded}
      className="group flex items-center gap-3 pl-4 pr-3 py-3 cursor-pointer hover:bg-surface-alt transition-colors"
    >
      <div className="flex-1 min-w-0">
        {editingField === "title" ? (
          <input
            type="text"
            value={editValue}
            onChange={(e) => onEditValueChange?.(e.target.value)}
            onBlur={() => onSaveEdit?.()}
            onKeyDown={(e) => e.key === "Enter" && onSaveEdit?.()}
            autoFocus
            className="w-full px-1 text-sm font-semibold bg-surface-alt border border-accent focus:outline-none"
          />
        ) : (
          <div
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              onStartEdit?.("title", event.title);
            }}
            className="font-medium text-sm text-ink truncate hover:text-accent cursor-pointer"
          >
            {event.title}
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-xs text-ink-muted font-mono">
            {event.startTime} -{" "}
            {event.endTime === "24:00" ? "00:00" : event.endTime}
          </span>
          {linkedTaskCount > 0 && (
            <span className={`inline-flex items-center gap-0.5 text-[10px] rounded-full px-1.5 py-px tabular-nums ${
              linkedDoneCount === linkedTaskCount
                ? "text-success/80 bg-success/10"
                : "text-ink-faint bg-surface-alt"
            }`}>
              <svg className="w-2.5 h-2.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="2" y="2" width="12" height="12" rx="2" />
                <path d="M5 8l2 2 4-4" />
              </svg>
              {linkedDoneCount}/{linkedTaskCount}
            </span>
          )}
        </div>
        <div className="text-[10px] text-ink-faint">
          {CATEGORY_LABELS[event.category]}
        </div>
      </div>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleExpanded();
        }}
        aria-label={isExpanded ? "Réduire détails" : "Afficher détails"}
        className={`shrink-0 min-w-6 min-h-6 flex items-center justify-center text-ink-faint hover:text-ink focus:outline-none transition-transform ${isExpanded ? "rotate-180" : ""}`}
      >
        ▼
      </button>

      {onDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label={`Supprimer: ${event.title}`}
          className="shrink-0 min-w-6 min-h-6 flex items-center justify-center text-ink-faint hover:text-danger focus:outline-none md:opacity-0 md:group-hover:opacity-100"
        >
          ✕
        </button>
      )}
    </div>
  );
}
